import React, { useState, useEffect, useRef } from 'react';
import { 
  Truck, Package, MapPin, Navigation, Camera, CheckCircle2, 
  Clock, Phone, MessageSquare, ArrowRight, RefreshCw, User,
  Store, Home as HomeIcon, AlertCircle, X, Upload, QrCode, Scan
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Store location (Merivale Road, Ottawa)
const STORE_LOCATION = {
  lat: 45.3456,
  lng: -75.7333,
  address: "1234 Merivale Road, Ottawa, ON"
};

const DriverDelivery = ({ onBack, user }) => {
  const [assignments, setAssignments] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-orders'); // 'my-orders', 'available', or 'scan'
  
  // Proof of delivery state
  const [showProofModal, setShowProofModal] = useState(false);
  const [photo1, setPhoto1] = useState(null);
  const [photo2, setPhoto2] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [submittingProof, setSubmittingProof] = useState(false);
  
  // QR Scanner state
  const [showScanner, setShowScanner] = useState(false);
  const [scannedOrder, setScannedOrder] = useState(null);
  
  const photo1Ref = useRef(null);
  const photo2Ref = useRef(null);
  const token = localStorage.getItem('auth_token');

  // Fetch driver's assignments
  const fetchAssignments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/driver/my-assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch assignments');
      const data = await res.json();
      setAssignments(data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch available orders
  const fetchAvailableOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/driver/available-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setAvailableOrders(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
    fetchAvailableOrders();
    const interval = setInterval(() => {
      fetchAssignments();
      fetchAvailableOrders();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Accept an order
  const acceptOrder = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/driver/accept-order/${orderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to accept order');
      toast.success('Order accepted!');
      fetchAssignments();
      fetchAvailableOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Update order status
  const updateStatus = async (orderId, endpoint, successMsg) => {
    try {
      const res = await fetch(`${API_URL}/api/driver/${endpoint}/${orderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to update status');
      toast.success(successMsg);
      fetchAssignments();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Handle photo capture
  const handlePhotoCapture = (e, photoNum) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (photoNum === 1) setPhoto1(event.target.result);
        else setPhoto2(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit proof of delivery
  const submitProofOfDelivery = async () => {
    if (!photo1 || !photo2) {
      toast.error('Please capture both photos');
      return;
    }

    setSubmittingProof(true);
    try {
      // Get current location
      let latitude = null, longitude = null;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      } catch (e) {
        console.log('Could not get location');
      }

      const res = await fetch(`${API_URL}/api/driver/deliver/${selectedOrder.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          photo1_base64: photo1,
          photo2_base64: photo2,
          notes: deliveryNotes,
          latitude,
          longitude
        })
      });

      if (!res.ok) throw new Error('Failed to complete delivery');
      
      toast.success('Delivery completed successfully!');
      setShowProofModal(false);
      setPhoto1(null);
      setPhoto2(null);
      setDeliveryNotes('');
      setSelectedOrder(null);
      fetchAssignments();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingProof(false);
    }
  };

  // Handle QR code scan - calls driver pickup API
  const handleQRScan = async (qrData) => {
    try {
      // Parse QR data - format: "UPLANDS-ORDER:ORDER_NUMBER"
      const match = qrData.match(/UPLANDS-ORDER:(.+)/);
      if (!match) {
        toast.error('Invalid QR code. Please scan an order QR code.');
        return;
      }
      
      const orderNumber = match[1];
      toast.info(`Processing order: ${orderNumber}`);
      
      // Call driver scan-pickup API to start delivery
      const res = await fetch(`${API_URL}/api/driver/scan-pickup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scanned_data: qrData })
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`✓ ${result.message}`);
        setShowScanner(false);
        
        // Refresh assignments
        await fetchAssignments();
        
        // Set scanned order for display
        const order = assignments.find(o => o.id === result.order_id);
        if (order) {
          setScannedOrder({ ...order, status: 'out_for_delivery' });
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process scan');
        
        // Still try to find and display the order
        const order = [...assignments, ...availableOrders].find(
          o => o.order_number === orderNumber || o.id.includes(orderNumber.toLowerCase())
        );
        if (order) {
          setScannedOrder(order);
        }
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Error processing QR code');
    }
  };

  // Start delivery from scanned order
  const startDelivery = async (order) => {
    try {
      // If order is not assigned to this driver, accept it first
      if (!assignments.find(a => a.id === order.id)) {
        await acceptOrder(order.id);
      }
      
      // Then start delivery (go to store)
      await updateStatus(order.id, 'go-to-store', 'Heading to store to pick up order!');
      setScannedOrder(null);
      setActiveTab('my-orders');
    } catch (error) {
      toast.error('Failed to start delivery');
    }
  };

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Open Google Maps for navigation
  const openGoogleMaps = (destination) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    window.open(url, '_blank');
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      'assigned': 'bg-blue-500',
      'driver_to_store': 'bg-yellow-500',
      'driver_at_store': 'bg-orange-500',
      'out_for_delivery': 'bg-purple-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  // Get status display text
  const getStatusText = (status) => {
    const texts = {
      'assigned': 'Assigned',
      'driver_to_store': 'Heading to Store',
      'driver_at_store': 'At Store',
      'out_for_delivery': 'Delivering',
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} />
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Truck className="w-6 h-6 text-blue-500" />
                  My Deliveries
                </h1>
                <p className="text-sm text-gray-500">{assignments.length} active orders</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => { fetchAssignments(); fetchAvailableOrders(); }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'my-orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('my-orders')}
            className="flex-1"
          >
            My Orders ({assignments.length})
          </Button>
          <Button
            variant={activeTab === 'scan' ? 'default' : 'outline'}
            onClick={() => setActiveTab('scan')}
            className="flex-1 bg-green-500 hover:bg-green-600"
          >
            <QrCode className="w-4 h-4 mr-2" />
            Scan QR
          </Button>
          <Button
            variant={activeTab === 'available' ? 'default' : 'outline'}
            onClick={() => setActiveTab('available')}
            className="flex-1"
          >
            Available ({availableOrders.length})
          </Button>
        </div>

        {/* Scan QR Tab */}
        {activeTab === 'scan' && (
          <div className="space-y-4">
            <Card className="p-4">
              <div className="text-center mb-4">
                <QrCode className="w-12 h-12 text-[#0a1628] mx-auto mb-2" />
                <h2 className="text-xl font-bold">Scan Order QR Code</h2>
                <p className="text-gray-500">Scan the QR code on the order to start delivery</p>
              </div>
              
              {/* Scan Button to Open Camera Scanner */}
              {!scannedOrder && (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="w-48 h-48 bg-gray-100 rounded-xl flex items-center justify-center border-4 border-dashed border-gray-300">
                    <Scan className="w-16 h-16 text-gray-400" />
                  </div>
                  <Button 
                    onClick={() => setShowScanner(true)}
                    className="bg-[#0a1628] gap-2"
                    data-testid="open-scanner-btn"
                  >
                    <Camera className="w-5 h-5" />
                    Scan Order QR Code
                  </Button>
                  <p className="text-sm text-gray-500 text-center max-w-xs">
                    Scan the QR code attached to the order at the store to confirm pickup and start delivery
                  </p>
                </div>
              )}
              
              {/* Scanned Order */}
              {scannedOrder && (
                <div className="mt-4 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="font-bold text-green-700">Order Scanned!</span>
                  </div>
                  
                  <div className="mb-3">
                    <p className="font-bold text-lg">#{scannedOrder.order_number}</p>
                    <p className="text-sm text-gray-600">{scannedOrder.delivery_info?.name}</p>
                    <p className="text-sm text-gray-600">{scannedOrder.delivery_info?.address}</p>
                    <p className="font-semibold mt-1">${scannedOrder.total?.toFixed(2)} • {scannedOrder.items?.length} items</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => startDelivery(scannedOrder)}
                      className="flex-1 bg-green-500 hover:bg-green-600 gap-2"
                    >
                      <Truck className="w-5 h-5" />
                      Start Delivery
                    </Button>
                    <Button 
                      onClick={() => setScannedOrder(null)}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Camera Barcode Scanner Modal */}
            <CameraBarcodeScanner
              isOpen={showScanner}
              onClose={() => setShowScanner(false)}
              onScan={(barcode) => {
                handleQRScan(barcode);
                setShowScanner(false);
              }}
              products={[]}
              onAddToCart={() => {}}
              mode="scan"
            />
          </div>
        )}

        {/* My Orders Tab */}
        {activeTab === 'my-orders' && (
          <div className="space-y-4">
            {assignments.length === 0 ? (
              <Card className="p-8 text-center">
                <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No active deliveries</p>
                <p className="text-sm text-gray-400 mt-1">Check available orders to accept new deliveries</p>
              </Card>
            ) : (
              assignments.map(order => (
                <Card key={order.id} className="p-4" data-testid={`delivery-order-${order.id}`}>
                  {/* Order Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold">#{order.order_number || order.id.slice(0, 8).toUpperCase()}</span>
                        <Badge className={getStatusColor(order.status)}>
                          {getStatusText(order.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">${order.total?.toFixed(2)} · {order.items?.length} items</p>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <Clock className="w-4 h-4 inline mr-1" />
                      {new Date(order.created_at).toLocaleTimeString()}
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="font-medium flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {order.delivery_info?.name}
                    </p>
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      {order.delivery_info?.address}, {order.delivery_info?.city}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" asChild>
                        <a href={`tel:${order.delivery_info?.phone}`}>
                          <Phone className="w-4 h-4 mr-1" /> Call
                        </a>
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <a href={`sms:${order.delivery_info?.phone}`}>
                          <MessageSquare className="w-4 h-4 mr-1" /> Text
                        </a>
                      </Button>
                    </div>
                  </div>

                  {/* Action Buttons based on status */}
                  <div className="space-y-2">
                    {order.status === 'assigned' && (
                      <>
                        <Button 
                          className="w-full bg-blue-500 hover:bg-blue-600"
                          onClick={() => {
                            updateStatus(order.id, 'heading-to-store', 'Heading to store...');
                            openGoogleMaps(STORE_LOCATION.address);
                          }}
                          data-testid={`heading-to-store-${order.id}`}
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Navigate to Store
                        </Button>
                      </>
                    )}

                    {order.status === 'driver_to_store' && (
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600"
                        onClick={() => updateStatus(order.id, 'arrived-at-store', 'Arrived at store!')}
                        data-testid={`arrived-store-${order.id}`}
                      >
                        <Store className="w-4 h-4 mr-2" />
                        I've Arrived at Store
                      </Button>
                    )}

                    {order.status === 'driver_at_store' && (
                      <Button 
                        className="w-full bg-purple-500 hover:bg-purple-600"
                        onClick={() => {
                          updateStatus(order.id, 'picked-up', 'Order picked up! Navigate to customer.');
                          const addr = `${order.delivery_info?.address}, ${order.delivery_info?.city}, ${order.delivery_info?.province}`;
                          openGoogleMaps(addr);
                        }}
                        data-testid={`picked-up-${order.id}`}
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Order Picked Up - Navigate to Customer
                      </Button>
                    )}

                    {order.status === 'out_for_delivery' && (
                      <>
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            const addr = `${order.delivery_info?.address}, ${order.delivery_info?.city}, ${order.delivery_info?.province}`;
                            openGoogleMaps(addr);
                          }}
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Open Navigation
                        </Button>
                        <Button 
                          className="w-full bg-green-500 hover:bg-green-600"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowProofModal(true);
                          }}
                          data-testid={`complete-delivery-${order.id}`}
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Complete Delivery (Take Photos)
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Available Orders Tab */}
        {activeTab === 'available' && (
          <div className="space-y-4">
            {availableOrders.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No available orders right now</p>
              </Card>
            ) : (
              availableOrders.map(order => (
                <Card key={order.id} className="p-4" data-testid={`available-order-${order.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="font-bold">#{order.id.slice(0, 8).toUpperCase()}</span>
                      <p className="text-sm text-gray-500">${order.total?.toFixed(2)} · {order.items?.length} items</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {order.delivery_info?.address}, {order.delivery_info?.city}
                  </p>
                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600"
                    onClick={() => acceptOrder(order.id)}
                    data-testid={`accept-order-${order.id}`}
                  >
                    Accept Order
                  </Button>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Proof of Delivery Modal */}
      {showProofModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-500" />
                Proof of Delivery
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowProofModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Take 2 photos as proof of delivery (e.g., package at door, delivered items)
            </p>

            {/* Photo 1 */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Photo 1 *</label>
              {photo1 ? (
                <div className="relative">
                  <img src={photo1} alt="Proof 1" className="w-full h-48 object-cover rounded-lg" />
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    className="absolute top-2 right-2"
                    onClick={() => setPhoto1(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500"
                  onClick={() => photo1Ref.current?.click()}
                >
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Tap to take photo</p>
                </div>
              )}
              <input
                ref={photo1Ref}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handlePhotoCapture(e, 1)}
              />
            </div>

            {/* Photo 2 */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Photo 2 *</label>
              {photo2 ? (
                <div className="relative">
                  <img src={photo2} alt="Proof 2" className="w-full h-48 object-cover rounded-lg" />
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    className="absolute top-2 right-2"
                    onClick={() => setPhoto2(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500"
                  onClick={() => photo2Ref.current?.click()}
                >
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Tap to take photo</p>
                </div>
              )}
              <input
                ref={photo2Ref}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handlePhotoCapture(e, 2)}
              />
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Delivery Notes (optional)</label>
              <Textarea
                placeholder="e.g., Left at front door, handed to customer..."
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                rows={2}
              />
            </div>

            {/* Submit Button */}
            <Button 
              className="w-full bg-green-500 hover:bg-green-600"
              disabled={!photo1 || !photo2 || submittingProof}
              onClick={submitProofOfDelivery}
              data-testid="submit-proof-btn"
            >
              {submittingProof ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              {submittingProof ? 'Submitting...' : 'Complete Delivery'}
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DriverDelivery;
